import axios from 'axios';
import API_BASE_URL from '../config/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token and user info
api.interceptors.request.use(
  (config) => {
    // Usar sessionStorage en lugar de localStorage para que la sesión se pierda al cerrar el navegador
    const token = sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Agregar información del usuario en headers personalizados para auditoría
    const userStr = sessionStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.UsuarioID || user.usuarioID) {
          config.headers['X-Usuario-ID'] = user.UsuarioID || user.usuarioID;
        }
        if (user.EmpresaID || user.empresaID) {
          const empresaId = user.EmpresaID || user.empresaID;
          config.headers['X-Empresa-ID'] = empresaId;
          config.params = { ...config.params, empresaId: empresaId };
        }
        if (user.SucursalID || user.sucursalID) {
          config.headers['X-Sucursal-ID'] = user.SucursalID || user.sucursalID;
        }
      } catch (e) {
        // Si hay error al parsear el usuario, continuar sin los headers
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    // Si la respuesta es un blob (archivo), devolverla tal cual sin procesar
    if (response.config.responseType === 'blob' || response.data instanceof Blob) {
      return response;
    }
    
    // API returns ApiResponse with StatusCode, Message, and Data
    // ASP.NET Core serializes to camelCase by default, but we handle both cases
    const apiResponse = response.data;
    
    // Get status code (handle both camelCase and PascalCase)
    const statusCode = apiResponse.StatusCode || apiResponse.statusCode || response.status;
    
    // Get message (handle both camelCase and PascalCase)
    const message = apiResponse.Message || apiResponse.message || '';
    
    // Get data (handle both camelCase and PascalCase)
    const data = apiResponse.Data || apiResponse.data || null;
    
    // Determine success based on status code
    const success = statusCode >= 200 && statusCode < 300;
    
    // Return normalized response
    return {
      StatusCode: statusCode,
      statusCode: statusCode,
      Message: message,
      message: message,
      Data: data,
      data: data,
      success: success,
    };
  },
  (error) => {
    // Handle network errors or non-JSON responses
    if (!error.response) {
      return Promise.reject({
        success: false,
        message: 'Error de conexión. Verifica que el servidor esté ejecutándose.',
        StatusCode: 0,
      });
    }

    // Handle HTTP errors
    const status = error.response.status;
    let message = 'Error al procesar la solicitud';

    // Try to extract message from response
    if (error.response.data) {
      if (typeof error.response.data === 'string') {
        // If response is HTML or plain text, don't try to parse it
        message = status === 401 
          ? 'Credenciales incorrectas' 
          : `Error del servidor (${status})`;
      } else if (error.response.data.Message) {
        message = error.response.data.Message;
      } else if (error.response.data.message) {
        message = error.response.data.message;
      }
    }

    // Handle 401 Unauthorized
    if (status === 401) {
      // Limpiar tanto sessionStorage como localStorage por si acaso
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Don't redirect here, let the component handle it
    }

    return Promise.reject({
      success: false,
      message,
      StatusCode: status,
      data: error.response.data?.Data || null,
    });
  }
);

export default api;

